import { constant } from '@config/BaseSetting';
import { serviceTypesAsync } from '@features/toolsRequestHistory/serviceTypesSlice';
import useLocalStorage from '@hooks/useLocalStorage';
import useUrlQuery from '@hooks/useUrlQuery';
import { useGetJobRequestsMutation } from '@services/WebApiService/jobRequestApi';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

const statusDescriptions = {
  1: 'Pending',
  2: 'Approved',
  3: 'Rejected',
};

function generateMatchText(search, serviceType, status) {
  let textData = '';
  if (search) {
    textData = `${
      !textData ? 'Matching' : `${textData}, `
    } Payload/Support ticket/Escalation ticket/Description like '%${search}%'`;
  }
  if (serviceType) {
    textData = `${!textData ? 'Matching' : `${textData}, `} Service type = ${serviceType}`;
  }
  if (status) {
    textData = `${!textData ? 'Matching' : `${textData}, `} Status = ${
      statusDescriptions[status] || status
    }`;
  }
  return textData;
}

const initialPagination = { pageIndex: 0, pageSize: 10 };

export default function useJobRequests() {
  const dispatch = useDispatch();
  const [accessToken] = useLocalStorage(constant.ACCESS_TOKEN_STORAGE);

  const [getJobRequests, getJobRequestsState] = useGetJobRequestsMutation();
  const { serviceTypesState } = useSelector((state) => state.serviceTypes);
  const { query, setQuery } = useUrlQuery();

  const [serviceTypeFilterState, setServiceTypeFilterState] = useState({
    search: '',
    id: '',
    name: '',
    loaded: [],
    shown: [],
  });
  const [statusFilterState, setStatusFilterState] = useState();

  const [fetchParams, setFetchParams] = useState({});
  const [pagination, setPagination] = useState(initialPagination);
  const [searchFieldValue, setSearchFieldValue] = useState('');
  const [matchText, setMatchText] = useState('');

  const loadData = () => {
    getJobRequests({
      accessToken,
      parameters: {
        search: fetchParams.search,
        jobRequestStatus: fetchParams.status,
        serviceType: fetchParams.serviceType,
        pageNumber: pagination.pageIndex + 1,
        pageSize: pagination.pageSize,
      },
    });
  };

  useEffect(() => {
    dispatch(serviceTypesAsync());
  }, []);

  useEffect(() => {
    const loaded = serviceTypesState.data.map((e, i) => {
      return {
        id: i,
        value: e['service-type-id'],
        title: e['service-type'],
      };
    });
    setServiceTypeFilterState({ ...serviceTypeFilterState, loaded });
  }, [serviceTypesState]);

  useEffect(() => {
    const newServiceTypeState = { ...serviceTypeFilterState };
    const idFromFragment = parseInt(query['service-type'], 10);
    let selectedServiceType;
    if (!idFromFragment.isNaN) {
      selectedServiceType = serviceTypesState.data?.find(
        (e) => e['service-type-id'] === idFromFragment
      );
      if (selectedServiceType) {
        newServiceTypeState.id = selectedServiceType['service-type-id'].toString();
        newServiceTypeState.name = selectedServiceType['service-type'];

        setMatchText(
          generateMatchText(
            searchFieldValue,
            selectedServiceType['service-type-id'],
            statusFilterState
          )
        );
      }
    }
    setFetchParams({
      search: searchFieldValue,
      serviceType: selectedServiceType?.['service-type-id'],
      status: statusFilterState,
    });
    newServiceTypeState.shown = newServiceTypeState.loaded;
    setServiceTypeFilterState(newServiceTypeState);
  }, [serviceTypeFilterState.loaded]);

  useEffect(() => {
    const shown = serviceTypeFilterState.loaded.filter((s) =>
      s.title?.toLowerCase().includes(serviceTypeFilterState.search?.toLowerCase())
    );
    setServiceTypeFilterState({ ...serviceTypeFilterState, shown });
  }, [serviceTypeFilterState.search]);

  useEffect(() => {
    const queryStatus = Number.parseInt(query.status, 10);
    if (!Number.isNaN(queryStatus)) {
      setStatusFilterState(queryStatus);
    }
  }, [query]);

  useEffect(loadData, [fetchParams, pagination]);

  const handleOnSearchFieldChange = (e) => {
    setSearchFieldValue(e.target.value);
  };

  const handleOnSearchClick = (e) => {
    e.preventDefault();
    setMatchText(
      generateMatchText(searchFieldValue, serviceTypeFilterState.name, statusFilterState)
    );
    setPagination(initialPagination);
    setFetchParams({
      search: searchFieldValue,
      serviceType: serviceTypeFilterState.id,
      status: statusFilterState,
    });
  };

  const handleOnResetClick = () => {
    setMatchText('');
    setSearchFieldValue('');
    setServiceTypeFilterState({
      ...serviceTypeFilterState,
      id: '',
    });
    setStatusFilterState(undefined);
    setQuery({ ...query, 'service-type': undefined, status: undefined });
    setPagination(initialPagination);
    setFetchParams({});
  };

  const handleOnRefreshClick = loadData;

  const handleOnServiceTypeChanged = (e) => {
    setServiceTypeFilterState({
      ...serviceTypeFilterState,
      id: e.target.value,
      name: serviceTypeFilterState.loaded.find((s) => s.value === e.target.value)?.title,
    });
    setQuery({ ...query, 'service-type': e.target.value });
  };

  const handleOnServiceTypeSearchChanged = (e) => {
    setServiceTypeFilterState({
      ...serviceTypeFilterState,
      search: e.target.value,
    });
  };

  const handleOnClearSearchServiceType = () => {
    setServiceTypeFilterState({
      ...serviceTypeFilterState,
      search: '',
    });
  };

  const handleOnStatusChanged = (e) => {
    const value = typeof e.target.value === 'object' ? e.target.value.value : e.target.value;
    setStatusFilterState(value);
    setQuery({ ...query, status: value });
  };

  return {
    searchFieldValue,
    matchText,
    pagination,
    serviceTypeFilterState,
    statusFilterState,
    getJobRequestsState,
    setPagination,
    handleOnSearchFieldChange,
    handleOnSearchClick,
    handleOnResetClick,
    handleOnRefreshClick,
    handleOnServiceTypeChanged,
    handleOnServiceTypeSearchChanged,
    handleOnClearSearchServiceType,
    handleOnStatusChanged,
  };
}
